import { Cached } from '@mui/icons-material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Grid, Box, SelectChangeEvent, List, ListItemButton } from '@mui/material';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormProvider, useForm } from 'react-hook-form';
import { HiOutlineUpload } from 'react-icons/hi';
import { amplitude, hasDownloadPermissions, IDownloadPermission } from 'wisestep-api';
import {
  IUserDTO,
  WsButton,
  WsColors,
  WsDrawer,
  WsElementWithPopOver,
  WsIconAssignCandidate,
  WsIconText,
} from 'wisestep-base';

import { IJob } from '../../../../../../wisestep-api/dist';
import { accessDeniedNotoficationTxt } from '../../../../components/AccessControl/AccessControl';
import { MenuListing } from '../../../../components/ExcelMenuList';
import {
  searchOptions,
  sortOptions,
} from '../../../../components/filters/components/AdvanceFilterHelper';
import WsSearchFilter from '../../../../components/WsSearchFilter';
import { useWorkspaceContext, useUserContext } from '../../../../context';
import MapAsSubmission from '../../../../features/jobs/listing/components/mapAsSubmission/MapAsSubmission';
import { useCandidateAts, CandidateAtsAccess } from '../../../../hooks/useCandidateAts';
import ErrorLogger from '../../../../providers/ErrorLogger';
import { warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../../utils/openUrlInNewTab';
import { TalentPoolSearchRequest } from '../../../candidates/talentpool/components/search/TalentPoolSearchRequest';
import AddCandidate from '../../../conversationWidget/components/MapAsLead';
import { ImportBulkSubmissions } from '../../../importCandidates/components/importSubmissions';
import { amplitudeEventAdvanceFilter, amplitudeEventRefresh } from '../../utils/AmplitudeEvents';

export interface JobsCrmSearchProps {
  onSearch: (data: TalentPoolSearchRequest) => void;
  type: string;
  job: IJob;
  clearAdvanceFilters: () => void;
  onSort: (event: SelectChangeEvent) => void;
  handleDownload: (type: string) => void;
}

const JobsCrmSearch = ({
  onSearch,
  type,
  job,
  clearAdvanceFilters,
  onSort,
  handleDownload,
}: JobsCrmSearchProps) => {
  const methods = useForm<TalentPoolSearchRequest>({ defaultValues: {} });
  const { handleSubmit } = methods;
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const [permissionState, setPermissionState] = useState<boolean>(false);
  const workspaceContext = useWorkspaceContext();
  const [downloadAnchor, setDownloadAnchor] = useState<null | HTMLElement>(null);
  const [openBulkUpload, setOpenBulkUpload] = useState<boolean>(false);
  const [openAddCandidate, setOpenAddCandidate] = useState<boolean>(false);
  const [openSubmissionForm, setOpenSubmissionForm] = useState<boolean>(false);
  const [resetKey, setResetKey] = useState(0);

  const setNotification = useNotificationStore((state) => state.setNotification);
  const openDownload = Boolean(downloadAnchor);

  const handleFormReset = () => setResetKey((prevKey) => prevKey + 1);

  const clearAllFields = () => {
    methods.reset();
    clearAdvanceFilters();
    handleFormReset();
  };

  const footerAmplitudeEvents = (name: string, clickedOnName: string) =>
    amplitude(name, {
      clickedOn: clickedOnName,
      workspaceName: workspaceContext.name,
      pageName: 'JobCRM',
      planType: workspaceContext.planType,
      interfaceType: 'web',
      pageUrl: window.location.href,
    });

  useEffect(() => {
    const permissionData = async () => {
      const permissionResponse = await hasDownloadPermissions();
      const permissionResponseData: IDownloadPermission = permissionResponse.data;
      setPermissionState(permissionResponseData.CS_DOWNLOAD_CANDIDATE_LIST);
    };
    permissionData();
  }, []);

  const handleOpenDownloadOptions = (event: React.MouseEvent<HTMLElement>) =>
    setDownloadAnchor(event.currentTarget);

  const handleCloseDownloadOptions = () => setDownloadAnchor(null);

  const onClickDownload = (type: string) => {
    handleDownload(type);
    handleCloseDownloadOptions();
  };
  const isViewDownload = useCandidateAts(CandidateAtsAccess.JOBCRM_DOWNLOAD);
  const isMapCandidate = useCandidateAts(CandidateAtsAccess.MAP_CANDIDATE);
  const isMapToAJob = useCandidateAts(CandidateAtsAccess.IMPORT_MAPPINGS_TO_JOB);
  const isSubmitToAJob = useCandidateAts(CandidateAtsAccess.IMPORT_SUBMISSION_TO_JOB);

  return (
    <>
      <FormProvider {...methods}>
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          <Box mt={2} component="form" onSubmit={handleSubmit(onSearch)}>
            <WsSearchFilter
              name={'JobsCrm'}
              showChildren={true}
              onClickFilter={() => {
                amplitudeEventAdvanceFilter[type](loggedInUser);
              }}
              showLeftActions={true}
              showDefaultRightActions={true}
              sortOptions={sortOptions}
              searchOptions={searchOptions}
              key={resetKey}
              searchPlaceholder="Search by Name, Email or Phone No"
              onSort={onSort}
              clearAllFields={clearAllFields}
              sortDefaultValue={'_score,desc'}
              isJobCrmSearch={true}
              leftActionsChildren={
                <Grid
                  container
                  item
                  pt={4}
                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <Grid item sx={{ display: 'flex' }}>
                    <WsButton
                      variant="text"
                      startIcon={<WsIconAssignCandidate color={WsColors.TheBlues500} height={25} />}
                      onClick={() => {
                        if (job?.isClosedForAssigningRecruiters) {
                          setNotification(
                            warning('Job is closed, please reopen job to add a candidate.')
                          );
                        } else {
                          footerAmplitudeEvents('Job_CRM_Add_Candidate', 'Add Candidate');
                          if (isMapCandidate) {
                            setOpenAddCandidate(true);
                          } else {
                            setNotification(warning(accessDeniedNotoficationTxt));
                          }
                        }
                      }}
                    >
                      Map As Lead
                    </WsButton>

                    <WsButton
                      variant="text"
                      startIcon={<DoneAllIcon />}
                      onClick={() => {
                        if (job?.isClosedForAssigningRecruiters) {
                          setNotification(
                            warning('Job is closed, Please reopen job to submit candidate.')
                          );
                        } else {
                          footerAmplitudeEvents('Job_Crm_New_Submit', 'Submit');
                          setOpenSubmissionForm(true);
                        }
                      }}
                    >
                      Submit
                    </WsButton>

                    <WsElementWithPopOver
                      element={
                        <WsButton
                          variant="text"
                          startIcon={<HiOutlineUpload color={WsColors.TheBlues500} />}
                        >
                          Import Candidates
                        </WsButton>
                      }
                    >
                      <List dense={true} sx={{ pl: 1 }}>
                        <ListItemButton>
                          <WsIconText
                            disable={job.isClosedForAssigningRecruiters}
                            text="Import Mappings"
                            onClick={() =>
                              isMapToAJob
                                ? openUrlInNewTab(`/import-candidates?jobId=${job.id}`)
                                : setNotification(warning(accessDeniedNotoficationTxt))
                            }
                            icon={<WsIconAssignCandidate />}
                            textCursor="pointer"
                          />
                        </ListItemButton>
                        <ListItemButton>
                          <WsIconText
                            disable={job.isClosedForAssigningRecruiters}
                            text="Import Submissions"
                            onClick={() =>
                              isSubmitToAJob
                                ? openUrlInNewTab(`/import-candidates/submission?jobId=${job.id}`)
                                : setNotification(warning(accessDeniedNotoficationTxt))
                            }
                            icon={<WsIconAssignCandidate />}
                            textCursor="pointer"
                          />
                        </ListItemButton>
                      </List>
                    </WsElementWithPopOver>
                  </Grid>

                  <Grid item sx={{ display: 'flex' }}>
                    <WsButton
                      variant="text"
                      startIcon={<Cached />}
                      type="submit"
                      onClick={() => amplitudeEventRefresh[type](loggedInUser)}
                    >
                      Refresh
                    </WsButton>

                    {permissionState &&
                      (workspaceContext?.owns ||
                        workspaceContext?.roles?.indexOf('ROLE_DOWNLOAD') !== -1) &&
                      isViewDownload && (
                        <WsButton
                          onClick={handleOpenDownloadOptions}
                          startIcon={<FileDownloadOutlinedIcon />}
                          variant="text"
                        >
                          Download
                        </WsButton>
                      )}
                    <MenuListing
                      downloadAnchor={downloadAnchor}
                      openDownload={openDownload}
                      handleCloseDownloadOptions={handleCloseDownloadOptions}
                      onClickDownload={onClickDownload}
                    />
                  </Grid>
                </Grid>
              }
            />
          </Box>
        </ErrorBoundary>
      </FormProvider>
      {openBulkUpload && (
        <ImportBulkSubmissions
          openBulkUpload={openBulkUpload}
          job={job}
          onClose={() => setOpenBulkUpload(false)}
        />
      )}
      {openAddCandidate && (
        <AddCandidate
          candidate={null}
          job={job}
          close={() => setOpenAddCandidate(false)}
          hideJobSelection={true}
          heading="Add Candidate"
        />
      )}
      {openSubmissionForm && (
        <>
          <WsDrawer
            style={{ zIndex: '1300' }}
            size="medium"
            hideHeader={true}
            open={openSubmissionForm}
            onClose={() => setOpenSubmissionForm(false)}
            hideOverflow={true}
          >
            <MapAsSubmission selectedJob={job} onClose={() => setOpenSubmissionForm(false)} />
          </WsDrawer>
        </>
      )}
    </>
  );
};
export default JobsCrmSearch;
